import React from 'react';
import { Image, StyleProp, StyleSheet, View, ViewStyle } from 'react-native';

/**
 * The word "homingo" on its own - no petal mark.
 *
 * Use where the symbol would be redundant because it already appears nearby,
 * or where a wide, short lockup fits the space better than a stacked one.
 * `Logo` is the full lockup; `LogoMark` is the symbol alone.
 *
 * There is no separate wordmark file, so this crops the lockup: the artwork is
 * drawn at its natural aspect inside a clipping box and offset upward so only
 * the lower band shows. Cheaper than shipping a fourth logo asset, and it
 * cannot drift out of sync with the other two.
 */

/** Intrinsic size of `logo_transparent.png`. */
const SRC_W = 215;
const SRC_H = 135;

/** The wordmark's bounding box within that artwork, measured off the file. */
const BOX = { x: 4, y: 75, w: 208, h: 56 };

type Props = {
  /** Cap height of the rendered wordmark in points; width follows. */
  height?: number;
  /**
   * Recolours the type. Omit to keep the artwork's own dark green.
   *
   * The wordmark is a bitmap, so this is `tintColor` rather than a fill - it
   * works because the artwork is a single flat colour on transparency, and
   * would flatten anything with more than one tone in it.
   */
  color?: string;
  style?: StyleProp<ViewStyle>;
};

function LogoWordmark({ height = 26, color, style }: Props) {
  // Source units to points. Everything else is this times a measured offset.
  const scale = height / BOX.h;

  return (
    <View
      style={[styles.clip, { width: BOX.w * scale, height }, style]}
      accessible
      accessibilityRole="image"
      accessibilityLabel="Homingo"
    >
      <Image
        source={require('../../assets/uploads/logo/logo_transparent.png')}
        style={{
          width: SRC_W * scale,
          height: SRC_H * scale,
          marginLeft: -BOX.x * scale,
          marginTop: -BOX.y * scale,
          tintColor: color,
        }}
        /*
         * "stretch", not "contain": the box is already the artwork's exact
         * aspect, so there is nothing to letterbox - and "contain" would
         * re-fit inside the offset frame and undo the crop.
         */
        resizeMode="stretch"
      />
    </View>
  );
}

const styles = StyleSheet.create({
  clip: {
    overflow: 'hidden',
    flexShrink: 0,
  },
});

export default LogoWordmark;
